/********************************************************************************
Filename: np-modal.js
Created: 27 Jan 2021
Author: Ron Robertson
Description: Displays a modal overlay to display additional data
Usage example:
    <np-modal id='myModal'>
        <h1 slot='title'>Modal Title</h1>
        <p>Modal content here</p>
    </np-modal>
Open Model Method:
  modalTrigger.addEventListener("click", myModal.open);
********************************************************************************/

(function() {
    const template = document.createElement('template');

    template.innerHTML = `
        <style>
            .np-modal-overlay {
                opacity:0;
                pointer-events:none;
                transition: opacity 0.5s ease-in-out;
                position: fixed;
                overflow: auto;
                top:0px;
                left:0px;
                right:0px;
                bottom:0px;
                background-color:rgba(0,0,0,0.8);
                z-index:850;
            }
            :host([opened]) .np-modal-overlay{
                opacity:1;
                pointer-events: all;
            }
            .np-modal-wrapper{
                opacity:0;
                pointer-events:none;
                top:50vh;
                transition-duration:0.7s;
                transition-timing-function:ease-in-out;
                transition-property:top,opacity;
                transition-delay:0.2s;
                position:relative;
                box-sizing:border-box;
                background-color:#ffffff;
                width: calc((var(--col-width) * 6) + (var(--gutter-width) * 5));
                padding:48px 20px;
                margin:50px auto;
                border-radius:8px;
                box-shadow: 0px 0px 120px rgba(25, 25, 25, 0.6);
            }
            :host([opened]) .np-modal-wrapper{
                top:0;
                opacity:1;
                pointer-events: all;
                transition-delay:0s;
            }
            .np-modal-close-trigger{
                position: absolute;
                top:48px;
                right:48px;
                background:none;
                color:inherit;
                border:none;
                padding:0;
                font:inherit;
                outline:inherit;
            }
            #title {
                padding:0px 58px 40px 48px;
                min-height:80px;
            }
            .np-modal-close-trigger:hover{
                cursor:pointer;
            }
            #modalBody{
                padding:0px 58px 40px 48px;
            }

            @media screen and (min-width: 768px) {
                .np-modal-wrapper{
                    width: calc((var(--col-width) * 10) + (var(--gutter-width) * 9));
                }
            }
            @media screen and (min-width: 960px) {
                .np-modal-wrapper{
                    width: calc((var(--col-width) * 10) + (var(--gutter-width) * 9));
                }
            }
            @media screen and (min-width: 1200px) {
                .np-modal-wrapper{
                    width: calc((var(--col-width) * 8) + (var(--gutter-width) * 9));
                }
            }

        </style>

        <div id="np-modal-overlay" class="np-modal-overlay">
            <div id="np-modal-wrapper" class="np-modal-wrapper">
                <button id="np-modal-close-trigger" class="np-modal-close-trigger" >
                    <svg width="44" height="42" viewBox="0 0 44 42" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <line x1="1.29289" y1="41.2929" x2="41.2929" y2="1.29289" stroke="#1D1D16" stroke-width="2"/>
                        <line x1="2.70711" y1="1.29289" x2="42.7071" y2="41.2929" stroke="#1D1D16" stroke-width="2"/>
                    </svg>
                </button>
                <div id="title"><slot name="title"></slot></div>
                <div id="modalBody"><slot name="body"></slot></div>
                <slot></slot>
            </div><!--END np-modal-wrapper-->
        </div><!--END np-modal-overlay-->
    `;

    class NpModal extends HTMLElement {
      constructor() {

        /* Always call super first in constructor */
        super();

        /* Activate the Shadow DOM */
        this.attachShadow({mode: 'open'});

        /* Keeps track if modal is open */
        this.isOpen = false;

        /* Attach template to DOM */
        this.shadowRoot.appendChild(template.content.cloneNode(true));

        /* Reference DOM Elements */
        this.overlay = this.shadowRoot.getElementById('np-modal-overlay');
        this.closeTrigger = this.shadowRoot.getElementById('np-modal-close-trigger');
        this.modalBody = this.shadowRoot.getElementById('modalBody');

      } /* END constructor */

        /* Called when added to DOM */
        connectedCallback() {
            /* Event Listeners */
            this.closeTrigger.addEventListener('click', this.close.bind(this));
            this.overlay.addEventListener('click', this._overlayClick.bind(this));
        }

      /* What to do when attributes are changed */
      attributesChangedCallback(name, oldValue, newValue) {
        if(this.hasAttribute('opened')) {
            this.isOpen=true;
        }else{
            this.isOpen=false;
        }
      }

      /* Attributes to monitor for changes */
      static get observedAttributes() {
          return ['opened'];
      }

      /* Called when removed from DOM */
      disconnectedCallback() {

        /* Removing event listeners */
        this.closeTrigger.removeEventListener('click', this.close);
        this.overlay.removeEventListener('click', this._overlayClick);
      }

      /* Private Methods */
        _overlayClick(e) {
            if(e.target === this.overlay) {
                this.close();
            }
        }

      /* Public Methods */
      open() {
        if(!this.hasAttribute('opened')) {
          this.setAttribute('opened','');
        }
          this.isOpen=true;
      }
      close() {
        if(this.hasAttribute('opened')) {
            this.removeAttribute('opened');
          }
          this.isOpen=false;

          /* Delay dispatching the close event to allow for animation to finish */
        this.timeout = setTimeout(function() {
            /* This event will bubble up the DOM tree */
            const modalClose = new Event('modalClose', {bubbles:true});
            this.dispatchEvent(modalClose);

            // scroll the body back to the top in case of reuse
            this.overlay.scrollTop = 0;

        }.bind(this), 300);

      }

    } /* END class */

    /* Export the class */
    window.customElements.define('np-modal', NpModal);

  })();
